/**
 * src/components/AdminAnnouncementBanner.js
 * 
 * User-facing component that displays active admin announcements and popups.
 * Supports:
 *  - Persistent banner at top of page (announcements)
 *  - Modal popup overlay (popup type)
 *  - Dismissable with optional feedback
 *  - Auto-hides dismissed announcements via localStorage
 * 
 * Place this inside the RootLayout or specific pages to show admin messages.
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Megaphone, X, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import * as adminService from "../services/adminService";
import { errorDev } from '../utils/logger.js';

const THEME_STYLES = {
  green: {
    shell: "from-green-700/95 via-green-600/95 to-emerald-600/95 dark:from-green-900/95 dark:via-green-800/95 dark:to-emerald-800/95",
    border: "border-green-300/40 dark:border-green-400/25",
    title: "text-white",
    message: "text-green-50",
    meta: "text-green-100/90",
  },
  blue: {
    shell: "from-sky-700/95 via-blue-600/95 to-cyan-600/95 dark:from-sky-900/95 dark:via-blue-800/95 dark:to-cyan-800/95",
    border: "border-sky-300/40 dark:border-sky-400/25",
    title: "text-white",
    message: "text-sky-50",
    meta: "text-sky-100/90",
  },
  red: {
    shell: "from-red-700/95 via-rose-600/95 to-red-600/95 dark:from-red-900/95 dark:via-rose-800/95 dark:to-red-800/95",
    border: "border-red-300/40 dark:border-red-400/25",
    title: "text-white",
    message: "text-red-50",
    meta: "text-red-100/90",
  },
};

function getDismissedKey(id) {
  return `admin_notif_dismissed_${id}`;
}

function isDismissed(id) {
  try {
    return localStorage.getItem(getDismissedKey(id)) === "1";
  } catch (err) {
    errorDev("Operation failed:", err);
    return false;
  }
}

function markDismissedLocal(id) {
  try {
    localStorage.setItem(getDismissedKey(id), "1");
  } catch (err) { errorDev("Operation failed:", err);}
}

function resolveAnnouncementIcon(iconName, priority) {
  const token = String(iconName || "").toLowerCase();
  if (token === "sparkles") return Sparkles;
  if (token === "alerttriangle" || token === "warning") return AlertTriangle;
  if (token === "megaphone") return Megaphone;
  if (priority === "urgent" || priority === "high") return AlertTriangle;
  return Megaphone;
}

function FloatingAnnouncement({ notification, onDismiss, currentUser }) {
  const theme = THEME_STYLES[notification.theme] || THEME_STYLES.green;
  const Icon = resolveAnnouncementIcon(notification.iconName, notification.priority);

  const handleDismiss = async () => {
    markDismissedLocal(notification.id);
    try {
      await adminService.dismissAdminNotification(notification.id, currentUser?.uid);
    } catch (err) { errorDev("Operation failed:", err);}
    onDismiss(notification.id);
  };

  useEffect(() => {
    adminService.markAdminNotificationRead(notification.id, currentUser?.uid).catch(() => {});
  }, [notification.id]);

  return (
    <div className="fixed left-3 right-3 top-[4.1rem] sm:left-1/2 sm:right-auto sm:w-[min(44rem,calc(100vw-2rem))] sm:-translate-x-1/2 md:hidden z-[55] animate-[fadeSlideIn_320ms_ease-out]">
      <div className={`relative overflow-hidden rounded-2xl border ${theme.border} bg-gradient-to-r ${theme.shell} shadow-[0_14px_35px_rgba(0,0,0,0.28)] backdrop-blur-md`}>
        <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
        <div className="relative px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/16 border border-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              {notification.title && (
                <p className={`text-sm sm:text-[15px] font-semibold leading-tight ${theme.title}`}>
                  {notification.title}
                </p>
              )}
              <p className={`mt-1 text-[13px] sm:text-sm leading-relaxed ${theme.message}`}>
                {notification.message}
              </p>
              {notification.imageUrl && (
                <img
                  src={notification.imageUrl}
                  alt={notification.title || "Announcement"}
                  className="mt-3 h-20 sm:h-24 w-full object-cover rounded-lg border border-white/25"
                  loading="lazy"
                />
              )}
              {notification.senderAlias && (
                <p className={`text-[11px] mt-2 inline-flex items-center gap-1.5 ${theme.meta}`}>
                  <ShieldCheck className="w-3 h-3" />
                  {notification.senderAlias}
                </p>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition flex items-center justify-center text-white"
              aria-label="Dismiss announcement"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────
export default function AdminAnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    let unsubNotifs = null;

    // Wait for auth to resolve before setting up the listener
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // Clean up previous notification listener on auth change
      if (unsubNotifs) {
        unsubNotifs();
        unsubNotifs = null;
      }

      if (!user) {
        setAnnouncements([]);
        setCurrentUser(null);
        return;
      }

      setCurrentUser(user);

      // User is authenticated — start listening
      unsubNotifs = adminService.listenActiveAnnouncements(user.uid, null, (notifs) => {
        const active = notifs
          .filter((n) => !isDismissed(n.id))
          .sort((a, b) => {
            const aTs = a.createdAt?.seconds || 0;
            const bTs = b.createdAt?.seconds || 0;
            return bTs - aTs;
          });
        setAnnouncements(active);
      });
    });

    return () => {
      unsubAuth();
      if (unsubNotifs) unsubNotifs();
    };
  }, []);

  const handleDismiss = useCallback((id) => {
    setAnnouncements((prev) => prev.filter((n) => n.id !== id));
    setActiveId(null);
  }, []);

  useEffect(() => {
    if (!announcements.length) {
      setActiveId(null);
      return;
    }
    if (!activeId || !announcements.find((n) => n.id === activeId)) {
      setActiveId(announcements[0].id);
    }
  }, [announcements, activeId]);

  const currentAnnouncement = useMemo(
    () => announcements.find((n) => n.id === activeId) || null,
    [announcements, activeId]
  );
  if (!currentAnnouncement) return null;

  return (
    <FloatingAnnouncement notification={currentAnnouncement} onDismiss={handleDismiss} currentUser={currentUser} />
  );
}
